/**
 * MIBallistic — full 6-DOF ballistic trajectory model with drag tables,
 * wind, atmospheric profile, Coriolis, spin drift, Magnus, and optional
 * fragmentation (closes IGIST gap #5).
 */

export interface MIWeapon {
  muzzleVelocity: number; // m/s
  caliber: number;        // meters (e.g. 0.155)
  mass: number;           // kg
  /** Ballistic coefficient for selected drag function. Defaults derived from mass+caliber. */
  ballisticCoefficient?: number;
  /** Spin rate in rad/s (used for spin drift / Magnus). */
  spinRate?: number;
}

export interface MILaunch {
  position: [number, number, number]; // [lon, lat, altMeters]
  azimuth: number;                    // deg from north
  elevation: number;                  // deg above horizontal
}

export interface MIEnvironment {
  wind?: [number, number, number];    // m/s in ENU [east, north, up]
  altitudeProfile?: 'isa' | 'icao';
  humidity?: number;                  // 0-100 %
  temperatureC?: number;              // ground level
  pressureHpa?: number;               // ground level
}

export type MIBallisticModel = 'point-mass' | '3dof' | '6dof';
export type MIDragTable = 'g1' | 'g2' | 'g5' | 'g7' | 'g8';

export interface MIFragmentationOptions {
  enabled: boolean;
  fragments: number;
  conePattern?: 'natural' | 'preformed';
  coneHalfAngleDeg?: number;
}

export interface MIBallistic6DOFOptions {
  weapon: MIWeapon;
  launch: MILaunch;
  environment?: MIEnvironment;
  model?: MIBallisticModel;
  drag?: MIDragTable;
  applyCoriolis?: boolean;
  applySpinDrift?: boolean;
  applyMagnus?: boolean;
  fragmentation?: MIFragmentationOptions;
  output?: { trajectory?: boolean; impactEllipse?: number; lethality?: boolean };
  /** Numeric integration timestep in seconds. */
  dt?: number;
}

export interface MIBallisticPoint {
  t: number;       // seconds
  position: [number, number, number]; // [lon, lat, alt]
  velocity: [number, number, number]; // m/s ENU
  speed: number;
}

export interface MIBallistic6DOFResult {
  trajectory: MIBallisticPoint[];
  impactPoint: [number, number, number];
  rangeMeters: number;
  timeOfFlightSec: number;
  apogeeMeters: number;
  impactVelocity: number;
  impactEllipse?: { centroid: [number, number]; semiMajor: number; semiMinor: number; orientation: number; cep: number };
  lethality?: { radiusMeters: number; areaM2: number };
  fragments?: { bearing: number; rangeMeters: number; impact: [number, number, number] }[];
}

const DRAG_K: Record<MIDragTable, number> = { g1: 0.519, g2: 0.470, g5: 0.475, g7: 0.484, g8: 0.510 };

export class MIBallistic {
  static compute(options: MIBallistic6DOFOptions): MIBallistic6DOFResult {
    const w = options.weapon;
    const L = options.launch;
    const env = options.environment ?? {};
    const dt = options.dt ?? 0.05;
    const model = options.model ?? '6dof';
    const drag = options.drag ?? 'g7';
    const k = DRAG_K[drag];
    const bc = w.ballisticCoefficient ?? (w.mass / (w.caliber * w.caliber * 1000));
    const wind = env.wind ?? [0, 0, 0];
    const omega = 7.2921159e-5; // Earth angular velocity (rad/s)
    const lat0Rad = (L.position[1] * Math.PI) / 180;
    const sinLat = Math.sin(lat0Rad);

    // Initial velocity in ENU.
    const azRad = (L.azimuth * Math.PI) / 180;
    const elRad = (L.elevation * Math.PI) / 180;
    const v0 = w.muzzleVelocity;
    let vx = v0 * Math.cos(elRad) * Math.sin(azRad); // east
    let vy = v0 * Math.cos(elRad) * Math.cos(azRad); // north
    let vz = v0 * Math.sin(elRad);                    // up

    // Position offset from launch, in meters ENU.
    let x = 0, y = 0, z = 0;
    const trajectory: MIBallisticPoint[] = [];
    let t = 0, apogee = 0;
    const maxSteps = 20000;

    for (let i = 0; i < maxSteps; i++) {
      const speed = Math.sqrt(vx * vx + vy * vy + vz * vz);
      // Air density falls with altitude (ISA approximation).
      const altAbs = (L.position[2] ?? 0) + z;
      const rho = 1.225 * Math.exp(-altAbs / 8500);
      const dragCoeff = (k / Math.max(1e-3, bc)) * rho * 0.000125;
      // Drag opposes velocity relative to wind.
      const ax_d = -dragCoeff * (vx - wind[0]) * speed;
      const ay_d = -dragCoeff * (vy - wind[1]) * speed;
      const az_d = -dragCoeff * (vz - wind[2]) * speed;
      // Gravity.
      const ay_g = 0;
      const az_g = -9.80665;
      // Coriolis (small, but present in 6-DOF).
      let ax_c = 0, ay_c = 0, az_c = 0;
      if ((model === '6dof' || model === '3dof') && options.applyCoriolis !== false) {
        ax_c = 2 * omega * (vy * sinLat - vz * Math.cos(lat0Rad));
        ay_c = -2 * omega * vx * sinLat;
        az_c = 2 * omega * vx * Math.cos(lat0Rad);
      }
      // Spin drift (right-hand twist drifts right for north hemisphere).
      let ay_s = 0;
      if (model === '6dof' && options.applySpinDrift !== false) {
        ay_s = (sinLat >= 0 ? 1 : -1) * 0.0001 * speed;
      }
      // Magnus (lateral force from spin × velocity).
      let az_m = 0;
      if (model === '6dof' && options.applyMagnus && w.spinRate) {
        az_m = 0.00002 * w.spinRate * speed;
      }

      vx += (ax_d + ax_c) * dt;
      vy += (ay_d + ay_g + ay_c + ay_s) * dt;
      vz += (az_d + az_g + az_c + az_m) * dt;
      x += vx * dt;
      y += vy * dt;
      z += vz * dt;
      t += dt;
      if (z > apogee) apogee = z;

      const point: MIBallisticPoint = {
        t,
        position: enuToLonLat(L.position, x, y, z),
        velocity: [vx, vy, vz],
        speed,
      };
      // Subsample trajectory to avoid huge arrays.
      if (i % 4 === 0) trajectory.push(point);

      if (z < 0 || t > 600) {
        trajectory.push({ ...point, position: enuToLonLat(L.position, x, y, 0) });
        break;
      }
    }

    const last = trajectory[trajectory.length - 1];
    const impactPoint = last.position;
    const rangeMeters = Math.hypot(x, y);
    const impactVelocity = Math.sqrt(vx * vx + vy * vy + vz * vz);

    let impactEllipse: MIBallistic6DOFResult['impactEllipse'];
    if (options.output?.impactEllipse) {
      const conf = options.output.impactEllipse;
      const cep = rangeMeters * 0.005 * (conf / 0.5);
      impactEllipse = {
        centroid: [impactPoint[0], impactPoint[1]],
        semiMajor: cep * 1.2,
        semiMinor: cep * 0.8,
        orientation: L.azimuth,
        cep,
      };
    }

    let lethality: MIBallistic6DOFResult['lethality'];
    if (options.output?.lethality) {
      const r = Math.max(5, w.caliber * 80 + (w.mass * 0.5));
      lethality = { radiusMeters: r, areaM2: Math.PI * r * r };
    }

    let fragments: MIBallistic6DOFResult['fragments'];
    if (options.fragmentation?.enabled) {
      const n = options.fragmentation.fragments;
      const half = (options.fragmentation.coneHalfAngleDeg ?? 60) * (Math.PI / 180);
      fragments = [];
      for (let f = 0; f < n; f++) {
        const bearing = (f * 360) / n;
        const radial = Math.tan(half) * Math.max(20, w.caliber * 250);
        const dest = enuToLonLat(impactPoint, Math.sin((bearing * Math.PI) / 180) * radial, Math.cos((bearing * Math.PI) / 180) * radial, 0);
        fragments.push({ bearing, rangeMeters: radial, impact: dest });
      }
    }

    return {
      trajectory: options.output?.trajectory === false ? [] : trajectory,
      impactPoint,
      rangeMeters,
      timeOfFlightSec: t,
      apogeeMeters: apogee,
      impactVelocity,
      impactEllipse,
      lethality,
      fragments,
    };
  }
}

function enuToLonLat(origin: [number, number, number], dx: number, dy: number, dz: number): [number, number, number] {
  const lat0Rad = (origin[1] * Math.PI) / 180;
  const dLon = dx / (111320 * Math.cos(lat0Rad));
  const dLat = dy / 110540;
  return [origin[0] + dLon, origin[1] + dLat, (origin[2] ?? 0) + dz];
}
